// lib
import React, { useContext, useState, useRef, useMemo, useCallback, useEffect } from 'react'
import PropTypes from 'prop-types'

import ADND from '@aytm/lib/adnd/ADND'

import { DataContext } from '@aytm/app/stats/page/question-views/vq/Component'

import { generateUID, encodeText, decodeText } from '@aytm/app/stats/lib/helpers'
import { getLogicData, getRefs, getClustersRefs } from '@aytm/app/stats/lib/refs'

// components
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'

import TextPopup from '@aytm/lib/rfd-ui/components/basic/text-popup'

import AdvancedFilterContainer from '@aytm/app/stats/page/components/AdvancedFilterContainer'

import AnswerVariantOptions from '@aytm/app/stats/page/question-views/vq/AnswerVariantOptions'

// hooks
import useSingleAndDoubleClick from '@aytm/app/stats/hooks/useSingleAndDoubleClick'

// styles
import style from '@aytm/app/stats/page/question-views/vq/styles/Component.module.scss'

const AnswerVariantMX = ({ item, position, dndItemsList }) => {
  const { id, data, disabled, dispatch, dropdownsRef, parserResultEof } = useContext(DataContext)

  const [baseMode, setBaseMode] = useState(false)

  const [basePattern, setBasePattern] = useState(item.base || '')

  const [selectedValue, setSelectedValue] = useState(null)

  const changedRef = useRef(0 < item.pattern.trim().length)

  const itemPatternRef = useRef(null)
  itemPatternRef.current = item.pattern

  const dndListItemRef = useRef()
  const dndListItemHandleRef = useRef()

  const parserResultPattern = useMemo(
    () =>
      data.parser.analyzeString({
        id: id,
        filter: item.pattern.trim(),
        clusters: data.clusters,
        saveTokens: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [item.pattern, id, data.surveySchema, data.clusters]
  )
  const parserResultPatternError = useMemo(
    () => 0 < item.pattern.trim().length && 0 < parserResultPattern.expression.status.errors.length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [parserResultPattern]
  )

  const [parserResultPatternBasic, setParserResultPatternBasic] = useState({})

  useEffect(() => {
    const tree = parserResultPatternError
      ? null
      : 0 < item.pattern.trim().length
      ? 0 < parserResultPattern.expression.status.errors.length
        ? null
        : { [item.pattern]: { x: true } }
      : {}
    setParserResultPatternBasic(null === tree || 1 < Object.keys(tree).length ? null : tree)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parserResultPattern, parserResultPatternError])

  const parserResultBase = useMemo(
    () =>
      data.parser.analyzeString({
        id: id,
        filter: basePattern,
        clusters: data.clusters,
        saveTokens: true,
        numberAllowed: true
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [id, data.surveySchema, data.clusters, basePattern]
  )
  const parserResultBaseError = useMemo(
    () =>
      0 < basePattern.length &&
      0 < parserResultBase.expression.status.errors.length &&
      parserResultBase.expression.status.errors.some(v => !['ref', 'cluster'].includes(v.type)),
    [basePattern, parserResultBase]
  )

  const textBase = useMemo(
    () =>
      (30 < item.base.length && 'Base: ' + item.base.slice(0, 27) + '...') ||
      (0 < item.base.length && 'Base: ' + item.base) ||
      'Base',
    [item.base]
  )

  const fetchData = useCallback(() => {
    return {
      id,
      item,
      position: position - 1,
      windowUID: data.windowUID,
      remove: () =>
        dispatch({
          type: 'DELETE_ANSWER_VARIANT',
          data: position - 1
        })
    }
  }, [item, position, id, data.windowUID, dispatch])

  const dndListItem = ADND.targets.itemsList.listItem({
    type: `dlq-answer-${data.windowUID}`,
    list: dndItemsList
  })
  const dndListItemHandle = ADND.sources
    .listItemHandle({
      type: `dlq-answer-${data.windowUID}`,
      item: dndListItem,
      cloneable: true
    })
    .fetchData(fetchData)

  const itemImageTemplate = option =>
    option ? (
      <>
        {option.src && <img width="23" height="23" src={option.src} alt="" />}
        <span dangerouslySetInnerHTML={{ __html: option.text }}></span>
      </>
    ) : (
      <i className="icon-aytmfd-image" />
    )
  const valueImageTemplate = option =>
    option && option.src ? <img width="23" height="23" src={option.src} alt="" /> : <i className="icon-aytmfd-image" />

  const onSelectedChange = (value = null, _values) => {
    const group = data.options.find(v => v.items.some(v => v.key == value))
    if (group.disabled) return

    const option = group.items.find(v => v.key == value) || null

    setSelectedValue(value)
    setBasePattern(value)

    setBaseMode(false)
    setBasePattern(value)

    changedRef.current = true

    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: {
          ...item,
          pattern: null === option ? '' : value,
          base: null === option ? '' : value
        },
        position: position - 1
      }
    })
  }

  const onLabelChange = event => {
    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: {
          ...item,
          text: encodeText(event.target.value)
        },
        position: position - 1
      }
    })
  }

  const onBaseChange = value => {
    setBaseMode(false)
    setBasePattern(value)

    item.base != value &&
      dispatch({
        type: 'UPDATE_ANSWER_VARIANT',
        data: {
          item: {
            ...item,
            base: value
          },
          position: position - 1
        }
      })
  }

  const onImageChange = event => {
    if ('off' == event.value) delete item.image
    else item.image = event.value

    dispatch({
      type: 'UPDATE_ANSWER_VARIANT',
      data: {
        item: {
          ...item
        },
        position: position - 1
      }
    })
  }

  const onDeleteClick = () => {
    dispatch({
      type: 'DELETE_ANSWER_VARIANT',
      data: position - 1
    })
  }

  const onHandleClick = useSingleAndDoubleClick(null, () => {
    dispatch({
      type: 'ADD_ANSWER_VARIANT',
      data: {
        item: {
          ...item,
          key: generateUID()
        },
        position: position
      }
    })
  })

  useEffect(() => {
    dropdownsRef.current[position] = selectedValue

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      delete dropdownsRef.current[position]
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [position, selectedValue]) // TODO

  useEffect(() => {
    if (changedRef.current) {
      changedRef.current = false
      return
    }

    const options = data.options.flatMap(v => v.items)
    const option =
      options.find(v => String(v.value || v.key).toLowerCase() == itemPatternRef.current.toLowerCase()) || null

    if (null === option) {
      setSelectedValue(null)
    } else {
      setSelectedValue(option.key)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parserResultPatternBasic])

  useEffect(() => {
    if (dndListItem) dndListItem.connect(dndListItemRef.current)

    return () => {
      if (dndListItem) dndListItem.disconnect()
    }
  }, [dndListItem])

  useEffect(() => {
    if (dndListItemHandle) dndListItemHandle.connect(dndListItemHandleRef.current)

    return () => {
      if (dndListItemHandle) dndListItemHandle.disconnect()
    }
  }, [dndListItemHandle])

  useEffect(() => {
    dispatch({
      type: 'UPDATE_LOGIC_DATA',
      data: {
        id: `a${position}.pattern`,
        logicData: getLogicData(data.tags, data.codeBooks, parserResultPattern, parserResultPatternError)
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_LOGIC_DATA',
        data: {
          id: `a${position}.pattern`,
          logicData: null
        }
      })
    }
  }, [position, data.tags, data.codeBooks, dispatch, parserResultPattern, parserResultPatternError])
  useEffect(() => {
    dispatch({
      type: 'UPDATE_LOGIC_DATA',
      data: {
        id: `a${position}.base`,
        logicData: getLogicData(data.tags, data.codeBooks, parserResultBase, parserResultBaseError)
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_LOGIC_DATA',
        data: {
          id: `a${position}.base`,
          logicData: null
        }
      })
    }
  }, [position, data.tags, data.codeBooks, dispatch, parserResultBase, parserResultBaseError])
  useEffect(() => {
    const patternRefs = getRefs(parserResultPattern)
    const baseRefs = getRefs(parserResultBase)
    const clustersRefs = getClustersRefs(data.modelClusters, parserResultPattern).concat(
      getClustersRefs(data.modelClusters, parserResultBase)
    )

    dispatch({
      type: 'UPDATE_REFS',
      data: {
        id: `a${position}`,
        refs: [patternRefs, baseRefs, clustersRefs]
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_REFS',
        data: {
          id: `a${position}`,
          refs: null
        }
      })
    }
  }, [position, data.modelClusters, dispatch, parserResultPattern, parserResultBase])
  useEffect(() => {
    dispatch({
      type: 'UPDATE_ERRORS',
      data: {
        id: `a${position}`,
        error: !item.pattern.trim().length || parserResultPatternError || parserResultBaseError
      }
    })

    return () => {
      dispatch({
        type: 'UPDATE_ERRORS',
        data: {
          id: `a${position}`,
          error: null
        }
      })
    }
  }, [item, position, dispatch, parserResultPatternError, parserResultBaseError])

  const renderOptions = () => (
    <AnswerVariantOptions
      position={position}
      value={selectedValue}
      options={data.options}
      dropdownsRef={dropdownsRef}
      onSelectedChange={onSelectedChange}
    />
  )

  const renderBaseMode = () => (
    <>
      <div className={`${style.base} p-field p-col-12`}>
        <AdvancedFilterContainer
          className="p-component p-inputtext"
          pattern={basePattern}
          parser={data.parser}
          locale={data.locale}
          surveySchema={data.surveySchema}
          clusters={data.clusters}
          parserResultEof={parserResultEof}
          prefix="base = "
          hintPosition="top"
          options={{ numberAllowed: true }}
          readOnly={disabled}
          onChange={disabled ? null : setBasePattern}
        />
      </div>
      <span className={style.clear} onClick={disabled ? null : () => onBaseChange('')} title="Clear">
        <i className="icon-aytmfd-delete" />
      </span>
      <span
        className={style.apply}
        onClick={disabled || parserResultBaseError ? null : () => onBaseChange(basePattern.trim())}
        title="Apply"
      >
        <i className="icon-aytmfd-checkmark" />
      </span>
    </>
  )
  const renderLabelMode = () => (
    <>
      {disabled && !item.base.length ? null : (
        <span
          className={`${style['group-toggle']}${disabled ? ' ' + style['group-toggle-disabled'] : ''}`}
          onClick={disabled ? null : () => setBaseMode(true)}
        >
          {30 < item.base.length ? (
            <TextPopup text={textBase} content={item.base} direction="bottomright" portal />
          ) : (
            textBase
          )}
        </span>
      )}
      <div className={`${style.images} p-field p-col-12`}>
        <InputText
          placeholder="Please type a label..."
          value={decodeText(item.text)}
          maxLength={120}
          disabled={disabled}
          onChange={disabled ? null : onLabelChange}
        />
      </div>
      <Dropdown
        className="p-dropdown-icon p-dropdown-right"
        placeholder=""
        value={item.image || ''}
        options={data.images}
        optionValue="key"
        optionLabel="text"
        itemTemplate={itemImageTemplate}
        valueTemplate={valueImageTemplate}
        fixedDropdownDir="bottom"
        disabled={disabled}
        onChange={disabled ? null : onImageChange}
      />
    </>
  )
  const renderModes = () => (baseMode ? renderBaseMode() : renderLabelMode())

  return (
    <div ref={dndListItemRef} className={`PRAYTMTheme ${style.row} ${style['row-av']} p-fluid p-col-12`}>
      {disabled ? null : (
        <span className={style.delete} onClick={onDeleteClick}>
          <i className="icon-aytmfd-delete" />
        </span>
      )}
      <div className={`${style.index} p-field`}>SQ{position}.</div>
      <div
        className={`${style.group} ${style['group-av']}${
          disabled ? ' ' + style['group-av-disabled'] : ''
        } p-fluid p-field p-col`}
      >
        <>
          <div className="p-fluid p-field p-col-4">{renderOptions()}</div>
          <div className={`${style.group} p-fluid p-field p-col`}>{renderModes()}</div>
        </>
      </div>
      {disabled ? null : (
        <div className="p-field">
          <span ref={dndListItemHandleRef} className={style.handle} onClick={onHandleClick}>
            <i className="icon-aytmfd-handle" />
          </span>
        </div>
      )}
    </div>
  )
}

AnswerVariantMX.propTypes = {
  item: PropTypes.object,
  position: PropTypes.number,
  dndItemsList: PropTypes.any
}

AnswerVariantMX.displayName = 'AnswerVariantMX'

export default AnswerVariantMX
